import { AxiosInstance } from "axios";
import QueryString from "qs";
import { Model } from "@iventis/domain-model/model/model";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { queryParams } from "@iventis/utilities";
import { Map } from "@iventis/domain-model/model/map";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Polygon } from "geojson";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { aggregatePagedResponse } from "@iventis/api-helpers";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { FilterFormat } from "@iventis/types";
import { LocalGeoJson } from "@iventis/map-types";

/**
 * Fetches all layers for a map using the paginated endpoint
 */
export const getMapLayers = async (mapId: string, mappingApi: AxiosInstance, signal?: AbortSignal): Promise<MapLayer[]> => {
    const layers = await aggregatePagedResponse<MapLayer[]>(mappingApi, `/maps/${mapId}/layers`, { signal }, async (cum, newLayers) => (cum ?? []).concat(newLayers));
    return layers;
};

export const getMap = async (
    mapId: string,
    projectId: string,
    mappingApi: AxiosInstance,
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    signal?: AbortSignal
): Promise<{ map: Map; localObjects: LocalGeoJson; projectDataFields: DataField[]; mapObjectBounds: Polygon }> => {
    const mapRequest = mappingApi
        .get<Map>(`/maps/${mapId}`, { signal })
        .catch((e) => {
            throw new Error(e);
        })
        .then((res) => res.data);

    // Fetch layers separately using the paginated endpoint
    const layersRequest = getMapLayers(mapId, mappingApi, signal);

    const localObjectsRequest = Promise.all([
        getLocalOnlyGeoJson(mappingApi, mapId, [], getWaypoints, signal),
        getLocalGeoJson(mappingApi, mapId, getWaypoints, [{ fieldName: "distanceMarker", operator: IventisFilterOperator.Eq, value: true }], signal),
    ]).then(([localOnlyObjects, distanceMarkerObjects]) => ({ ...localOnlyObjects, ...distanceMarkerObjects }));

    const projectDataFieldsRequest = mappingApi.get<DataField[]>(`/projects/${projectId}/data-fields`);

    const boundsRequest = getMapObjectBounds(mappingApi, mapId, queryParams.level);

    const [map, layers, localObjects, { data: projectDataFields }, { data: mapObjectBounds }] = await Promise.all([
        mapRequest,
        layersRequest,
        localObjectsRequest,
        projectDataFieldsRequest,
        boundsRequest,
    ]);

    // Add layers to the map object
    map.layers = layers;

    return { map, localObjects, projectDataFields, mapObjectBounds };
};
export const getLocalOnlyGeoJson = async (
    mappingApi: AxiosInstance,
    mapId: string,
    additionalFilters: FilterFormat[],
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    signal?: AbortSignal
) => {
    // Request for local only map objects (e.g. 3D models)
    const filter = [{ fieldName: "StyleType", operator: IventisFilterOperator.In, value: [StyleType.Model, StyleType.LineModel, StyleType.AreaModel] }, ...additionalFilters];
    const objects = await getLocalGeoJson(mappingApi, mapId, getWaypoints, filter, signal);
    return objects;
};

export const getLocalGeoJson = async (
    mappingApi: AxiosInstance,
    mapId: string,
    getWaypoints: (objectId: string, mapId: string) => Promise<RouteWaypoint[]>,
    filter: FilterFormat[],
    signal?: AbortSignal
) => {
    const request = {
        params: {
            filter: JSON.stringify(filter),
            mapId,
        },
        paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
    };
    const objects = await aggregatePagedResponse<MapObject[], LocalGeoJson>(mappingApi, `/maps/${mapId}/map_objects/filter`, { ...request, signal }, async (cum, objects) => {
        const waypoints = await Promise.all(
            objects.map<Promise<RouteWaypoint[]>>(async (object) => {
                let waypoints: RouteWaypoint[] | null = null;
                if ((object.modeOfTransport ?? object.geoJsonFeature?.properties?.modeOfTransport) != null) {
                    waypoints = await getWaypoints(object.id, mapId);
                }
                return waypoints;
            })
        );
        return objects
            .map((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]))
            .reduce((acc, object) => {
                const { layerid } = object.feature.properties;
                const existingObjects = acc[layerid];
                acc[layerid] = [...(existingObjects ?? []), object];
                return acc;
            }, cum ?? {});
    });
    return objects;
};

/**
 * Gets multiple models by their ids, only one request will be made for each unique id
 *
 * Has to be here due to a circular dependency redux issue
 */
export const multipleModelsGetter = async (ids: string[], mappingApi: AxiosInstance, signal?: AbortSignal) => {
    const models = await mappingApi.get<Model[]>(`models/lods`, {
        // Get all the unique ids so we aren't requesting the same model multiple times
        params: { ids: ids.reduce((acc, id) => (acc.includes(id) ? acc : [...acc, id]), []) },
        paramsSerializer: (params) => QueryString.stringify(params, { arrayFormat: "repeat" }),
        signal,
    });
    return models.data;
};

// Get the bounding box of map objects on a map
// Always includes non-visible layers when getting the bounds for all map objects as otherwise when the user makes the layers visible, the map will not think there are objects on the map tiles
export async function getMapObjectBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    return mappingApi.get<Polygon>(`/maps/${mapId}/bounding-box?level=${level ?? 0}&includeHiddenLayers=true`, { signal });
}

export async function getCommentBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    return mappingApi.get<Polygon>(`/maps/${mapId}/comments/bounding-box?level=${level ?? 0}`, { signal });
}

export async function getMapObjectAndCommentBounds(mappingApi: AxiosInstance, mapId: string, level: string, signal?: AbortSignal) {
    const mapObjectBoundsRequest = getMapObjectBounds(mappingApi, mapId, level, signal);

    const commentBoundsRequest = getCommentBounds(mappingApi, mapId, level, signal);

    let [{ data: mapObjectBounds }, { data: commentBounds }] = await Promise.all([mapObjectBoundsRequest, commentBoundsRequest]);

    // If the map object bounds is an empty string, set it to undefined
    // This is because the API returns an empty string when there are no map objects
    if (typeof mapObjectBounds === "string") {
        mapObjectBounds = undefined;
    }
    if (typeof commentBounds === "string") {
        commentBounds = undefined;
    }

    return { mapObjectBounds, commentBounds };
}
